import Phaser from 'phaser';
import type { ButtonOptions } from '../../types/ui';
import { AudioManager } from '../../systems/AudioManager';
import { FocusManager } from '../../systems/FocusManager';
import { PlayerPreferences } from '../../systems/PlayerPreferences';
import { VisualTheme } from '../VisualTheme';

export function createTextButton(scene: Phaser.Scene, options: ButtonOptions) {
  const preferences = PlayerPreferences.instance;
  const width = options.width ?? 260;
  const height = options.height ?? 52;
  const highContrast = preferences.value.highContrast;
  const shadow = scene.add.rectangle(4, 6, width, height, VisualTheme.colors.shadow, highContrast ? 0.8 : 0.5).setOrigin(0.5);
  const bg = scene.add.rectangle(0, 0, width, height, highContrast ? 0x05080c : VisualTheme.colors.panel, 0.98)
    .setStrokeStyle(highContrast ? 3 : 2, highContrast ? VisualTheme.colors.goldBright : VisualTheme.colors.gold, 1)
    .setOrigin(0.5);
  const inner = scene.add.rectangle(0, 0, width - 8, height - 8, 0x000000, 0)
    .setStrokeStyle(1, VisualTheme.colors.goldBright, highContrast ? 0.62 : 0.18)
    .setOrigin(0.5);
  const accent = scene.add.rectangle(-width / 2 + 4, 0, highContrast ? 7 : 4, height - 12, highContrast ? preferences.semanticColor('information') : VisualTheme.colors.jadeBright, 0.9).setOrigin(0.5);
  const label = scene.add.text(0, 0, options.label, {
    fontFamily: VisualTheme.fonts.display,
    fontSize: `${preferences.fontSize(Math.round((options.fontSize ?? 21) * preferences.value.uiScale))}px`,
    color: VisualTheme.text.title,
    stroke: '#120c08',
    strokeThickness: highContrast ? 2 : 1,
  }).setOrigin(0.5);

  const container = scene.add.container(options.x, options.y, [shadow, bg, inner, accent, label]);
  container.setSize(width, height);
  container.setInteractive(new Phaser.Geom.Rectangle(-width / 2, -height / 2, width, height), Phaser.Geom.Rectangle.Contains);
  if (container.input) container.input.cursor = 'pointer';

  let keyboardFocused = false;
  const setScale = (value: number, duration: number) => {
    scene.tweens.killTweensOf(container);
    if (preferences.value.reducedMotion || duration === 0) container.setScale(value);
    else scene.tweens.add({ targets: container, scaleX: value, scaleY: value, duration: preferences.tweenDuration(duration), ease: 'Sine.Out' });
  };
  const setFocused = (focused: boolean) => {
    if (!container.active) return;
    keyboardFocused = focused;
    bg.setStrokeStyle(focused || highContrast ? 3 : 2, focused ? 0xffffff : highContrast ? VisualTheme.colors.goldBright : VisualTheme.colors.gold, 1);
    inner.setStrokeStyle(focused ? 2 : 1, focused ? preferences.semanticColor('information') : VisualTheme.colors.goldBright, focused ? 1 : highContrast ? 0.62 : 0.18);
    label.setColor(focused ? '#ffffff' : VisualTheme.text.title);
    if (focused) setScale(1.025, 80);
  };
  const activate = () => {
    if (!container.active) return;
    bg.setFillStyle(VisualTheme.colors.panelPressed, 1);
    setScale(0.985, 45);
    AudioManager.instance.playUi(scene);
    options.onClick();
  };

  container.on('pointerover', () => {
    bg.setFillStyle(highContrast ? 0x182638 : VisualTheme.colors.panelHover, 1);
    bg.setStrokeStyle(3, VisualTheme.colors.goldBright, 1);
    accent.setFillStyle(VisualTheme.colors.goldBright, 1);
    label.setColor('#fff8df');
    setScale(1.025, 90);
  });
  container.on('pointerout', () => {
    bg.setFillStyle(highContrast ? 0x05080c : VisualTheme.colors.panel, 0.98);
    accent.setFillStyle(highContrast ? preferences.semanticColor('information') : VisualTheme.colors.jadeBright, 0.9);
    setFocused(keyboardFocused);
    if (!keyboardFocused) setScale(1, 110);
  });
  container.on('pointerdown', activate);

  FocusManager.instance.register(scene, { activate, setFocused, isActive: () => container.active });
  return container;
}
